import { Button } from "@fluentui/react-components";
import { useEffect, useState, type KeyboardEvent } from "react";
import { useIntl } from "react-intl";
import { DialogView } from "@/components/atoms/dialog/DialogView";
import { DialogSize } from "@/components/atoms/dialog/types";
import { ContactFilterValue, ContactsAdvancedFilter } from "@/repository/models";
import { AdvancedFilterField } from "./AdvancedFilterField";
import { useStyle } from "./style";
import {
	AdvancedFilterDialogProps,
	AdvancedFilterFieldKey,
	advancedFilterFields,
} from "./types";

export const AdvancedFilterDialog = (
	props: AdvancedFilterDialogProps
): JSX.Element => {
	const { open, setOpen, value, onApply } = props;
	const intl = useIntl();
	const classes = useStyle();

	const [draft, setDraft] = useState<ContactsAdvancedFilter>(value);

	// Reopening the dialog always starts from what is actually filtering, so a cancelled edit is discarded.
	useEffect(() => {
		if (open) {
			setDraft(value);
		}
	}, [open, value]);

	const onFieldChange = (
		key: AdvancedFilterFieldKey,
		values: ContactFilterValue[]
	): void => {
		setDraft((current: ContactsAdvancedFilter) => ({
			...current,
			[key]: values,
		}));
	};

	const apply = (): void => {
		onApply(draft);
		setOpen(false);
	};

	// Clicking the dialog rather than a field leaves focus on the dialog itself, and Enter there means the
	// same as Enter in a field. Only the dialog's own key counts: everything pressed on a field, a chip's
	// remove button or either button below bubbles through here as well, and belongs to what it was pressed on.
	const onDialogKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
		if (event.key === "Enter" && event.target === event.currentTarget) {
			apply();
		}
	};

	const content = (
		<>
			<div className={classes.dialogContent}>
				{advancedFilterFields.map((definition) => (
					<AdvancedFilterField
						key={definition.key}
						field={definition.field}
						label={intl.formatMessage({ id: definition.labelId })}
						selected={draft[definition.key]}
						onChange={(values: ContactFilterValue[]) =>
							onFieldChange(definition.key, values)
						}
						// Enter applies, the way it does on the search box behind this dialog. The field
						// answers the key itself while it still has a value to take, and hands it over
						// once there is nothing left to pick. Asking the field rather than listening on
						// the whole form leaves Enter on the chips' remove buttons to those buttons.
						onEnter={apply}
					/>
				))}
			</div>
			<div className={classes.buttonsContainer}>
				<Button onClick={() => setOpen(false)}>
					{intl.formatMessage({ id: "Common.Cancel" })}
				</Button>
				<Button appearance="primary" onClick={apply}>
					{intl.formatMessage({ id: "Contacts.AdvancedFilter.Apply" })}
				</Button>
			</div>
		</>
	);

	return (
		<DialogView
			open={open}
			setOpen={setOpen}
			size={DialogSize.Medium}
			title={intl.formatMessage({ id: "Contacts.AdvancedFilter.Title" })}
			content={content}
			onKeyDown={onDialogKeyDown}
		/>
	);
};
